// Story 5.5 — FR59 confirm-modal for marketplace purchase.
//
// Mounted at page level by MarketplacePage when a listing card is
// clicked. Carries the full preview (creator + tier + edition for shares;
// portfolio + edition for sealed dossiers) + price + a mono provenance
// block (sellers are anonymous by design — no seller row) + balance impact
// (UX-DR24 always-visible balance). The buyer simply pays the listed price;
// the 10/5/85 resale split is a seller-side concern (surfaced in the
// list-for-sale flow), so no fee breakdown is shown here. Clicking the
// scrim closes via the same handleClose path as Cancel (so post-success
// invalidation runs).
//
// State machine maps the buy mutation's branched 4xx codes onto distinct
// UI states:
//   idle              → Buy button armed.
//   submitting        → spinner; Buy disabled.
//   succeeded         → "Your purchase is complete" + View-in-vault Link.
//   insufficient_balance → in-modal alert + Top up CTA linking to
//                          /account/wallet (the interrupt+resume flow
//                          per the existing Story 2.10 BuyDossierPage
//                          pattern is parked for a v2 polish).
//   listing_inactive  → "Sold/cancelled/expired" message; Cancel returns
//                       to browse; no retry.
//   self_buy          → "Cannot buy your own listing" — narrow guard.
//   error             → generic error caption + Cancel.
//
// On modal close after success, invalidate ["vault-items"] + ["wallet-balance"]
// + the per-surface marketplace listings query so the just-bought row
// disappears from the browse grid.

import {
  Button,
  PatronageCertificate,
  SealedDossier,
  formatIssueNumber,
} from "@greenhold/components";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  formatCents,
  SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS,
  SHARE_LISTING_PRICE_MIN_CENTS,
} from "./feeMath";
import { MakeOfferDialog } from "./MakeOfferDialog";
import {
  certPropsFromShareRow,
  dossierFromListingRow,
  type MarketplaceListingCardListing,
} from "./MarketplaceListingCard";
import {
  useBuyShareListing,
  type BuyShareListingError,
  type BuyShareListingResponseData,
} from "./useBuyShareListing";
import {
  useBuySealedDossierListing,
  type BuySealedDossierListingError,
  type BuySealedDossierListingResponseData,
} from "./useBuySealedDossierListing";
import { useListingLiveState } from "./useListingLiveState";

export type BuyListingConfirmModalStatus =
  | "idle"
  | "submitting"
  | "succeeded"
  | "insufficient_balance"
  | "listing_inactive"
  | "self_buy"
  | "error";

export interface BuyListingConfirmModalProps {
  listing: MarketplaceListingCardListing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current wallet balance in cents. Passed in by the page-level mount
   * (which fetches via useQuery on /wallet/balance). null when still
   * loading. */
  balanceCents: number | null;
  /** Owner-only: open the Edit-listing flow for one's own listing. When
   *  provided, the own-listing branch shows an "Edit listing" affordance. */
  onEditListing?: (listing: MarketplaceListingCardListing) => void;
}

type AnyBuyError = BuyShareListingError | BuySealedDossierListingError;
type AnyBuyResponse =
  | BuyShareListingResponseData
  | BuySealedDossierListingResponseData;

function statusFromError(err: AnyBuyError): BuyListingConfirmModalStatus {
  switch (err.code) {
    case "INSUFFICIENT_BALANCE":
      return "insufficient_balance";
    case "LISTING_NOT_ACTIVE":
    case "LISTING_NOT_FOUND":
      return "listing_inactive";
    case "CANNOT_BUY_OWN_LISTING":
      return "self_buy";
    default:
      return "error";
  }
}

function listingDescriptor(listing: MarketplaceListingCardListing): {
  itemTitle: string;
  itemSubtitle: string;
  priceCents: number;
} {
  if (listing.kind === "share") {
    const row = listing.row;
    return {
      itemTitle: row.share.creator_display_name ?? row.share.creator_handle ?? "Creator",
      itemSubtitle: `${row.share.tier.toUpperCase()} • Issue ${formatIssueNumber(row.share.edition_number)}`,
      priceCents: row.price_cents,
    };
  }
  const row = listing.row;
  return {
    itemTitle: row.sealed_dossier.portfolio_title,
    itemSubtitle: `Issue ${formatIssueNumber(row.sealed_dossier.edition_number)} • Sealed dossier`,
    priceCents: row.price_cents,
  };
}

export function BuyListingConfirmModal(props: BuyListingConfirmModalProps) {
  const { listing, open, onOpenChange, balanceCents, onEditListing } = props;
  const queryClient = useQueryClient();
  const desc = listingDescriptor(listing);

  const shareMutation = useBuyShareListing();
  const sealedMutation = useBuySealedDossierListing();

  const [status, setStatus] = useState<BuyListingConfirmModalStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shortfallCents, setShortfallCents] = useState<number | null>(null);
  const [response, setResponse] = useState<AnyBuyResponse | null>(null);
  const [showOffer, setShowOffer] = useState(false);
  // After the buyer refreshes an updated listing, the seller's new values
  // override the (now-stale) prop the card was clicked with.
  const [override, setOverride] = useState<{
    price: number;
    allowBidding: boolean;
  } | null>(null);

  const isOwnListing = listing.row.is_own_listing;

  // The price the buyer currently sees + agrees to (the prop, unless refreshed).
  const effectivePrice = override?.price ?? desc.priceCents;
  const effectiveAllowBidding = override?.allowBidding ?? listing.row.allow_bidding;

  // Poll the listing while the buyer is deciding (idle, not their own listing).
  // A seller edit or a sale-by-someone-else is surfaced as a banner instead of
  // letting the buyer pay a stale amount.
  const live = useListingLiveState(
    listing.kind,
    listing.row.listing_id,
    open && status === "idle" && !isOwnListing,
  );
  const liveData = live.data;
  const listingGone =
    status === "idle" &&
    !isOwnListing &&
    liveData != null &&
    liveData.status !== "active";
  const listingChanged =
    status === "idle" &&
    !isOwnListing &&
    liveData?.status === "active" &&
    (liveData.price_cents !== effectivePrice ||
      liveData.allow_bidding !== effectiveAllowBidding);

  function refreshToLive() {
    if (liveData?.status === "active") {
      setOverride({
        price: liveData.price_cents,
        allowBidding: liveData.allow_bidding,
      });
    }
    void live.refetch();
  }

  const balanceAfter =
    balanceCents !== null ? balanceCents - effectivePrice : null;

  function handleOfferPlaced() {
    // Placing an offer holds funds (balance → escrow) but leaves the listing
    // active. Refresh the wallet + the buyer's offers, then close both.
    queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
    queryClient.invalidateQueries({ queryKey: ["my-offers"] });
    setShowOffer(false);
    onOpenChange(false);
  }

  function invalidateRelatedQueries() {
    queryClient.invalidateQueries({ queryKey: ["vault-items"] });
    queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
    if (listing.kind === "share") {
      queryClient.invalidateQueries({
        queryKey: ["marketplace-share-listings"],
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: ["marketplace-sealed-dossier-listings"],
      });
    }
  }

  async function handleBuy() {
    setStatus("submitting");
    setErrorMessage(null);
    try {
      if (listing.kind === "share") {
        const res = await shareMutation.mutateAsync({
          listing_id: listing.row.listing_id,
          expected_price_cents: effectivePrice,
        });
        setResponse(res);
        setStatus("succeeded");
      } else {
        const res = await sealedMutation.mutateAsync({
          listing_id: listing.row.listing_id,
          expected_price_cents: effectivePrice,
        });
        setResponse(res);
        setStatus("succeeded");
      }
    } catch (err) {
      const buyErr = err as AnyBuyError;
      // The seller edited the price between our last poll and this click:
      // drop back to idle and re-read so the "listing updated" banner shows,
      // rather than surfacing a generic error.
      if (buyErr.code === "LISTING_PRICE_CHANGED") {
        setStatus("idle");
        setErrorMessage(null);
        await live.refetch();
        return;
      }
      const next = statusFromError(buyErr);
      setStatus(next);
      setErrorMessage(buyErr.message ?? null);
      if (
        next === "insufficient_balance" &&
        typeof buyErr.required_cents === "number" &&
        typeof buyErr.available_cents === "number"
      ) {
        setShortfallCents(buyErr.required_cents - buyErr.available_cents);
      }
    }
  }

  function handleClose() {
    // After success, refresh dependent queries so the SPA reflects the
    // ownership transfer + balance change.
    if (status === "succeeded") {
      invalidateRelatedQueries();
    }
    setStatus("idle");
    setErrorMessage(null);
    setShortfallCents(null);
    setResponse(null);
    setOverride(null);
    onOpenChange(false);
  }

  if (!open) return null;

  return (
    <>
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="buy-confirm-title"
      aria-describedby="buy-confirm-desc"
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink-900/60 p-4"
      onClick={handleClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl border border-brand-gold-500/40 bg-brand-paper-50 p-6 text-brand-ink-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[11px] uppercase tracking-wider text-brand-gold-600">
          Listing detail
        </p>
        <h2
          id="buy-confirm-title"
          className="mt-1 font-heading text-xl font-bold text-brand-ink-900"
        >
          Confirm your purchase
        </h2>
        <p id="buy-confirm-desc" className="mt-1 text-sm text-brand-ink-700">
          {/* For sealed listings the artifact face in the display inset
              below carries the portfolio title, so the description line
              keeps only the subtitle (avoids duplicated title text). */}
          {listing.kind === "share"
            ? `${desc.itemTitle} · ${desc.itemSubtitle}`
            : desc.itemSubtitle}
        </p>

        {/* Dark display inset (ListingDetail.dc.html): forest-900 panel,
            gold hairline, rounded 12, artifact centered. The dark ground
            is reserved for artifact display — the panel chrome stays
            paper. */}
        <div className="mt-4 flex justify-center rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 px-6 py-6">
          {listing.kind === "share" ? (
            // Full 320×452 certificate scaled to fit the dark inset.
            <PatronageCertificate
              width={200}
              {...certPropsFromShareRow(listing.row.share)}
            />
          ) : (
            // Same 200px footprint as the share certificate above so both
            // listing kinds present identically in the inset.
            <div className="w-[200px]">
              <SealedDossier
                dossier={dossierFromListingRow(listing.row)}
                size="md"
              />
            </div>
          )}
        </div>

        {/* Provenance — the design's mono dl block: muted labels left,
            full-contrast values right. Sellers are anonymous by design, so
            there is no seller row; shares surface the collector-friendly
            per-creator serial + the cert's dossier/tier/condition datapoints
            (the GH- uuid serial stays on the certificate face). */}
        <div className="mt-4 rounded-md border border-brand-paper-300 bg-brand-paper-100 p-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-brand-gold-600">
            Provenance
          </p>
          <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 font-mono text-xs">
            {listing.kind === "share" ? (
              <>
                <dt className="text-brand-ink-700/70">Serial</dt>
                <dd className="m-0 text-right text-brand-ink-900">
                  #{listing.row.share.share_serial}
                </dd>
                <dt className="text-brand-ink-700/70">Dossier</dt>
                <dd className="m-0 text-right text-brand-ink-900">
                  {listing.row.share.portfolio_title}
                </dd>
                <dt className="text-brand-ink-700/70">Tier</dt>
                <dd className="m-0 text-right text-brand-ink-900">
                  {listing.row.share.tier.toLowerCase()}
                </dd>
                <dt className="text-brand-ink-700/70">Condition</dt>
                <dd className="m-0 text-right text-brand-ink-900">
                  {listing.row.share.condition.toFixed(8)}
                </dd>
              </>
            ) : (
              <>
                <dt className="text-brand-ink-700/70">Issue</dt>
                <dd className="m-0 text-right text-brand-ink-900">
                  #{formatIssueNumber(listing.row.sealed_dossier.edition_number)}
                </dd>
              </>
            )}
            <dt className="text-brand-ink-700/70">Listed</dt>
            <dd className="m-0 text-right text-brand-ink-900">
              {listing.row.listed_at.slice(0, 10)}
            </dd>
          </dl>
        </div>

        {/* Price / balance breakdown */}
        <dl className="mt-4 space-y-1 rounded-md border border-brand-paper-300 p-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-brand-ink-700">Price</dt>
            <dd className="font-mono tabular-nums">{formatCents(effectivePrice)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-brand-ink-700">Current balance</dt>
            <dd className="font-mono tabular-nums" data-testid="balance-current">
              {balanceCents !== null ? formatCents(balanceCents) : "—"}
            </dd>
          </div>
          <div className="flex justify-between border-t border-brand-paper-200 pt-1 font-semibold">
            <dt>Balance after</dt>
            <dd className="font-mono tabular-nums" data-testid="balance-after">
              {balanceAfter !== null ? formatCents(balanceAfter) : "—"}
            </dd>
          </div>
        </dl>

        {/* State-aware action area */}
        <div aria-live="polite" className="mt-4 space-y-2">
          {status === "idle" && isOwnListing && (
            <>
              <p
                className="rounded-md border border-brand-paper-300 bg-brand-paper-200 p-2 text-center text-sm text-brand-ink-800"
                data-testid="own-listing-message"
              >
                This is your own listing — you can&apos;t buy or bid on it.
              </p>
              {onEditListing && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    onEditListing(listing);
                    handleClose();
                  }}
                  className="w-full"
                  data-testid="edit-own-listing-button"
                >
                  Edit listing
                </Button>
              )}
            </>
          )}
          {/* Someone else bought it / the seller withdrew it while this modal
              was open. */}
          {status === "idle" && !isOwnListing && listingGone && (
            <p
              className="rounded-md bg-brand-paper-200 p-2 text-sm text-brand-ink-800"
              data-testid="listing-gone-message"
            >
              This listing is no longer available.
            </p>
          )}
          {/* The seller edited the price / bidding while this modal was open —
              require an explicit refresh before buying so the price never
              changes under the buyer mid-click. */}
          {status === "idle" && !isOwnListing && !listingGone && listingChanged && (
            <div
              className="space-y-2 rounded-md border border-state-warning-fill/50 bg-brand-paper-200 p-3"
              data-testid="listing-updated-banner"
            >
              <p className="text-sm text-brand-ink-900">
                The seller updated this listing.{" "}
                {liveData && liveData.price_cents !== effectivePrice && (
                  <>
                    New price:{" "}
                    <strong>{formatCents(liveData.price_cents)}</strong>.
                  </>
                )}
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={refreshToLive}
                className="w-full"
                data-testid="refresh-listing-button"
              >
                Refresh
              </Button>
            </div>
          )}
          {status === "idle" &&
            !isOwnListing &&
            !listingGone &&
            !listingChanged && (
              <>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleBuy}
                  className="w-full"
                >
                  Buy — {formatCents(effectivePrice)}
                </Button>
                {effectiveAllowBidding && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowOffer(true)}
                    className="w-full border border-brand-gold-500/50"
                    data-testid="make-offer-button"
                  >
                    Make an offer
                  </Button>
                )}
              </>
            )}
          {status === "submitting" && (
            <Button type="button" variant="primary" disabled className="w-full">
              Processing…
            </Button>
          )}
          {status === "succeeded" && response && (
            <>
              <p className="text-sm font-semibold text-brand-forest-600">
                Your purchase is complete.
              </p>
              <Link
                to={response.vault_redirect_url}
                onClick={handleClose}
                className="inline-block w-full rounded-md bg-brand-forest-500 px-4 py-2 text-center font-medium text-brand-paper-50 hover:bg-brand-forest-600"
              >
                View in collection
              </Link>
            </>
          )}
          {status === "insufficient_balance" && (
            <>
              <p
                className="rounded-md border border-state-warning-fill/50 bg-brand-paper-200 p-2 text-sm text-brand-ink-900"
                data-testid="insufficient-balance-alert"
              >
                Your balance is too low for this purchase.
                {shortfallCents !== null && (
                  <>
                    {" "}
                    You need <strong>{formatCents(shortfallCents)}</strong> more.
                  </>
                )}
              </p>
              <Link
                to="/account/wallet"
                className="inline-block w-full rounded-md bg-state-warning-fill px-4 py-2 text-center font-medium text-state-warning-fg hover:brightness-110"
              >
                Top up your wallet
              </Link>
            </>
          )}
          {status === "listing_inactive" && (
            <p
              className="rounded-md bg-brand-paper-200 p-2 text-sm text-brand-ink-800"
              data-testid="listing-inactive-message"
            >
              This listing is no longer available.
              {errorMessage ? ` (${errorMessage})` : ""}
            </p>
          )}
          {status === "self_buy" && (
            <p
              className="rounded-md bg-brand-paper-200 p-2 text-sm text-brand-ink-800"
              data-testid="self-buy-message"
            >
              You can't buy your own listing.
            </p>
          )}
          {status === "error" && (
            <p
              className="rounded-md border border-state-error-fill/50 bg-brand-paper-200 p-2 text-sm text-state-error-fill"
              data-testid="generic-error"
            >
              {errorMessage ?? "Something went wrong. Try again."}
            </p>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="w-full text-sm text-brand-ink-700 underline decoration-dotted hover:text-brand-ink-900"
          >
            {status === "succeeded" ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
    {showOffer && (
      <MakeOfferDialog
        open={showOffer}
        onOpenChange={setShowOffer}
        kind={listing.kind}
        listingId={listing.row.listing_id}
        itemNoun={listing.kind === "share" ? "certificate" : "sealed dossier"}
        askingPriceCents={effectivePrice}
        minCents={
          listing.kind === "share"
            ? SHARE_LISTING_PRICE_MIN_CENTS
            : SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS
        }
        balanceCents={balanceCents}
        onPlaced={handleOfferPlaced}
      />
    )}
    </>
  );
}
